import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import { getDeviceList } from "api/devices.api";
import { MAX_ROWS_FETCH, NUMBER_OF_ROWS_PER_PAGE } from "config/constants";
import { min } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "stores/RootStore";
import { Device, DeviceListResponse } from "types/device";
import { KeyValuePair } from "utils/common";

export class DeviceListStore {
  root: RootStore;
  loading = true;
  error: ErrorResponseDto | null = null

  deviceList: Device[] = [];
  //this is for loading 200 records and FE paginating when the sort order in based on unpaginated sort fields
  catchedDeviceList: Device[] = [];
  totalCount: number = 0;
  nextToken: string = ""
  searchTerm: string = "";
  searchCategory: string = "";
  searchCategoriesList: KeyValuePair[] = [
    { value: 'device_id_filter', key: 'Device ID' },
    { value: 'location_name_filter', key: 'Location' },
  ]
  sortField: string = "last_update"
  sortDirection: "asc" | "desc" = "desc"

  constructor(root: RootStore) {
    this.root = root;
    makeObservable(this, {
      loading: observable,
      error: observable,
      totalCount: observable,
      deviceList: observable,
      searchTerm: observable,
      searchCategory: observable,
      searchCategoriesList: observable,
      sortField: observable,
      sortDirection: observable,

      loadDevices: action.bound,
      setSearchTerm: action.bound,
      setSearchCategory: action.bound,
      reset: action.bound,
      onSort: action.bound,

      nextCount: computed,
      getSearchCategoryTitle: computed,
      getEmptyListMsg: computed
    });
  }

  reset() {
    this.searchTerm = ""
    this.searchCategory = ''
    this.deviceList = []
    this.nextToken = ''
    this.loading = true
    this.error = null
  }

  setSearchTerm(search: string) {
    this.searchTerm = search
  }
  setSearchCategory(cat: string) {
    this.searchCategory = cat
  }

  get getSearchCategoryTitle() {
    return this.searchCategoriesList.find(r => r.value === this.searchCategory)?.key || ""
  }

  frontendPagination() {
    return this.sortField === "last_update"
  }

  getLoadParams(firstPage: boolean) {
    const params: Record<string, any> = {
      limit: this.frontendPagination() ? MAX_ROWS_FETCH : NUMBER_OF_ROWS_PER_PAGE
    }
    if (this.searchTerm !== "") {
      if (this.searchCategory !== "") {
        params[this.searchCategory] = this.searchTerm;
      }
      else {
        this.searchCategoriesList.forEach(c => {
          params[c.value] = this.searchTerm
        })
      }
    }
    if (this.sortField !== "") {
      params.sort = `${this.sortDirection === "desc" ? '-' : ''}${this.sortField}`
    }
    if (!firstPage && this.nextToken !== "")
      params.next = this.nextToken

    return params
  }

  loadDevices(firstPage = false) {
    this.loading = true
    this.error = null

    if (!firstPage && this.frontendPagination()) {
      const pageNumber = this.deviceList.length / NUMBER_OF_ROWS_PER_PAGE
      const start = pageNumber * NUMBER_OF_ROWS_PER_PAGE
      const end = ((pageNumber + 1) * NUMBER_OF_ROWS_PER_PAGE)
      this.deviceList = [...this.deviceList, ...this.catchedDeviceList.slice(start, end)]
      this.loading = false
      return Promise.resolve()
    }

    return getDeviceList(this.getLoadParams(firstPage))
      .then((res: DeviceListResponse) => {
        if (firstPage) {
          if (this.frontendPagination()) {
            this.deviceList = [...res.items.slice(0, NUMBER_OF_ROWS_PER_PAGE)]
            this.catchedDeviceList = res.items
          }
          else {
            this.deviceList = [...res.items]
          }
        }
        else {
          this.deviceList = [...this.deviceList, ...res.items]
        }
        this.totalCount = res.total
        this.nextToken = res.next
      })
      .catch((err: ErrorResponseDto) => this.error = err)
      .finally(() => this.loading = false)
  }

  onSort(field: string, direction?: string) {
    if (direction) {
      this.sortDirection = direction as any
    }
    else {
      this.sortDirection = this.sortField !== field
        ? "asc"
        : this.sortDirection === "asc" ? "desc" : "asc"
    }
    this.sortField = field
    this.loadDevices(true)
  }

  get nextCount() {
    return min([this.totalCount - this.deviceList.length, NUMBER_OF_ROWS_PER_PAGE]) || 0
  }

  get getEmptyListMsg() {
    if (this.searchTerm === "")
      return "No Devices to display"
    else
      return `Sorry, no results were found for ${this.searchTerm}
        ${this.searchCategory !== ""
          ? ` in <b>${this.searchCategoriesList.find(c => c.value === this.searchCategory)?.key}</b>`
          : ''
        }`
  }

}
